import React, { useState, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import InterviewRequestCard from './components/InterviewRequestCard';
import ScheduleInterviewModal from './components/ScheduleInterviewModal';
import InterviewDetailsModal from './components/InterviewDetailsModal';
import ReminderModal from './components/ReminderModal';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = {
  'en-US': enUS
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

const InterviewSchedulingManagement = () => {
  const [view, setView] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const mockInterviewRequests = [
  {
    id: 1,
    candidateName: 'Rahul Sharma',
    candidatePhoto: 'https://img.rocket.new/generatedImages/rocket_gen_img_1ef2015b3-1763301538793.png',
    candidatePhotoAlt: 'Professional headshot of young Indian man in formal attire',
    position: 'Frontend Developer',
    status: 'pending',
    appliedDate: '2026-02-01',
    preferredDates: ['2026-02-05', '2026-02-06'],
    preferredTime: 'Morning (10 AM - 12 PM)',
    email: 'rahul.sharma@example.com',
    phone: '+91 98765 43210',
    experience: '2 years',
    skills: ['React', 'Node.js', 'MongoDB']
  },
  {
    id: 2,
    candidateName: 'Priya Patel',
    candidatePhoto: 'https://img.rocket.new/generatedImages/rocket_gen_img_1ee2bcef3-1763301295581.png',
    candidatePhotoAlt: 'Professional portrait of young woman with long dark hair',
    position: 'Backend Developer',
    status: 'scheduled',
    appliedDate: '2026-01-30',
    scheduledDate: '2026-02-05T10:00:00',
    meetingLink: 'https://meet.google.com/abc-defg-hij',
    meetingPlatform: 'Google Meet',
    email: 'priya.patel@example.com',
    phone: '+91 98765 43211',
    experience: '3 years',
    skills: ['Python', 'Django', 'PostgreSQL']
  },
  {
    id: 3,
    candidateName: 'Amit Kumar',
    candidatePhoto: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a7d4290a-1763293336648.png',
    candidatePhotoAlt: 'Professional photo of man in business casual attire',
    position: 'Full Stack Developer',
    status: 'scheduled',
    appliedDate: '2026-01-28',
    scheduledDate: '2026-02-06T14:00:00',
    meetingLink: 'https://zoom.us/j/123456789',
    meetingPlatform: 'Zoom',
    email: 'amit.kumar@example.com',
    phone: '+91 98765 43212',
    experience: '1 year',
    skills: ['Java', 'Spring Boot', 'MySQL']
  },
  {
    id: 4,
    candidateName: 'Sneha Reddy',
    candidatePhoto: 'https://img.rocket.new/generatedImages/rocket_gen_img_12672b149-1763294392419.png',
    candidatePhotoAlt: 'Professional headshot of woman with confident smile',
    position: 'UI/UX Designer',
    status: 'completed',
    appliedDate: '2026-01-25',
    scheduledDate: '2026-02-02T11:00:00',
    completedDate: '2026-02-02T12:00:00',
    meetingLink: 'https://teams.microsoft.com/l/meetup-join',
    meetingPlatform: 'Microsoft Teams',
    email: 'sneha.reddy@example.com',
    phone: '+91 98765 43213',
    experience: '4 years',
    skills: ['Figma', 'Adobe XD', 'Sketch']
  },
  {
    id: 5,
    candidateName: 'Vikram Singh',
    candidatePhoto: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a58c58e8-1763294332999.png',
    candidatePhotoAlt: 'Professional portrait of man in formal shirt',
    position: 'DevOps Engineer',
    status: 'rescheduled',
    appliedDate: '2026-01-22',
    originalDate: '2026-02-03T15:00:00',
    scheduledDate: '2026-02-07T10:00:00',
    meetingLink: 'https://meet.google.com/xyz-abcd-efg',
    meetingPlatform: 'Google Meet',
    email: 'vikram.singh@example.com',
    phone: '+91 98765 43214',
    experience: '5 years',
    skills: ['Docker', 'Kubernetes', 'AWS']
  }];


  const mockEvents = [
  {
    id: 2,
    title: 'Priya Patel - Backend Developer',
    start: new Date(2026, 1, 5, 10, 0),
    end: new Date(2026, 1, 5, 11, 0),
    status: 'scheduled',
    candidateId: 2
  },
  {
    id: 3,
    title: 'Amit Kumar - Full Stack Developer',
    start: new Date(2026, 1, 6, 14, 0),
    end: new Date(2026, 1, 6, 15, 0),
    status: 'scheduled',
    candidateId: 3
  },
  {
    id: 4,
    title: 'Sneha Reddy - UI/UX Designer',
    start: new Date(2026, 1, 2, 11, 0),
    end: new Date(2026, 1, 2, 12, 0),
    status: 'completed',
    candidateId: 4
  },
  {
    id: 5,
    title: 'Vikram Singh - DevOps Engineer',
    start: new Date(2026, 1, 7, 10, 0),
    end: new Date(2026, 1, 7, 11, 0),
    status: 'rescheduled',
    candidateId: 5
  }];


  const [events, setEvents] = useState(mockEvents);
  const [interviewRequests, setInterviewRequests] = useState(mockInterviewRequests);

  const eventStyleGetter = (event) => {
    let backgroundColor = 'var(--color-primary)';

    switch (event?.status) {
      case 'scheduled':
        backgroundColor = 'var(--color-primary)';
        break;
      case 'completed':
        backgroundColor = 'var(--color-success)';
        break;
      case 'rescheduled':
        backgroundColor = 'var(--color-warning)';
        break;
      case 'cancelled':
        backgroundColor = 'var(--color-error)';
        break;
      default:
        backgroundColor = 'var(--color-muted)';
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  const handleSelectSlot = useCallback((slotInfo) => {
    setSelectedDate(slotInfo?.start);
    setShowScheduleModal(true);
  }, []);

  const handleSelectEvent = useCallback((event) => {
    const interview = interviewRequests?.find((req) => req?.id === event?.candidateId);
    setSelectedInterview(interview);
    setShowDetailsModal(true);
  }, [interviewRequests]);

  const handleScheduleInterview = (candidate) => {
    setSelectedCandidate(candidate);
    setShowScheduleModal(true);
  };

  const handleSendReminder = (interview) => {
    setSelectedInterview(interview);
    setShowReminderModal(true);
  };

  const filteredRequests = interviewRequests?.filter((req) => {
    const matchesStatus = filterStatus === 'all' || req?.status === filterStatus;
    const matchesSearch = req?.candidateName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    req?.position?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const stats = [
  {
    label: 'Total Interviews',
    value: interviewRequests?.length,
    icon: 'Calendar',
    color: 'var(--color-primary)'
  },
  {
    label: 'Scheduled',
    value: interviewRequests?.filter((r) => r?.status === 'scheduled')?.length,
    icon: 'Clock',
    color: 'var(--color-success)'
  },
  {
    label: 'Pending',
    value: interviewRequests?.filter((r) => r?.status === 'pending')?.length,
    icon: 'AlertCircle',
    color: 'var(--color-warning)'
  },
  {
    label: 'Completed',
    value: interviewRequests?.filter((r) => r?.status === 'completed')?.length,
    icon: 'CheckCircle',
    color: 'var(--color-accent)'
  }];


  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Interview Scheduling & Management</h1>
            <p className="text-muted-foreground mt-1">Coordinate interviews with calendar integration and automated reminders</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              iconName="Bell"
              onClick={() => setShowReminderModal(true)}>

              Send Reminders
            </Button>
            <Button
              iconName="Plus"
              onClick={() => setShowScheduleModal(true)}>

              Schedule Interview
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats?.map((stat, index) =>
          <div key={index} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat?.label}</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{stat?.value}</p>
                </div>
                <div className="p-3 rounded-full" style={{ backgroundColor: `${stat?.color}20` }}>
                  <Icon name={stat?.icon} size={24} color={stat?.color} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Content - Dual Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Panel */}
          <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground">Interview Calendar</h2>
              <div className="flex items-center gap-2">
                <Button
                  variant={view === 'month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setView('month')}>

                  Month
                </Button>
                <Button
                  variant={view === 'week' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setView('week')}>

                  Week
                </Button>
                <Button
                  variant={view === 'day' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setView('day')}>

                  Day
                </Button>
              </div>
            </div>

            <div className="calendar-container" style={{ height: '600px' }}>
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                view={view}
                onView={setView}
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventStyleGetter}
                selectable
                popup
                style={{ height: '100%' }} />

            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'var(--color-primary)' }}></div>
                <span className="text-sm text-muted-foreground">Scheduled</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'var(--color-success)' }}></div>
                <span className="text-sm text-muted-foreground">Completed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'var(--color-warning)' }}></div>
                <span className="text-sm text-muted-foreground">Rescheduled</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'var(--color-error)' }}></div>
                <span className="text-sm text-muted-foreground">Cancelled</span>
              </div>
            </div>
          </div>

          {/* Interview Requests Panel */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-4">Interview Requests</h2>

            {/* Search and Filter */}
            <div className="space-y-3 mb-4">
              <div className="relative">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search candidates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />

              </div>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring">

                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="rescheduled">Rescheduled</option>
              </select>
            </div>

            {/* Request List */}
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {filteredRequests?.length === 0 ?
              <div className="text-center py-8">
                  <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No interview requests found</p>
                </div> :

              filteredRequests?.map((request) =>
              <InterviewRequestCard
                key={request?.id}
                request={request}
                onSchedule={handleScheduleInterview}
                onSendReminder={handleSendReminder}
                onViewDetails={(req) => {
                  setSelectedInterview(req);
                  setShowDetailsModal(true);
                }} />

              )
              }
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showScheduleModal &&
      <ScheduleInterviewModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setSelectedCandidate(null);
        }}
        candidate={selectedCandidate}
        selectedDate={selectedDate}
        onSchedule={(scheduleData) => {
          console.log('Schedule data:', scheduleData);
          setShowScheduleModal(false);
          setSelectedCandidate(null);
        }} />

      }

      {showDetailsModal && selectedInterview &&
      <InterviewDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedInterview(null);
        }}
        interview={selectedInterview}
        onReschedule={(interview) => {
          setShowDetailsModal(false);
          setSelectedCandidate(interview);
          setShowScheduleModal(true);
        }}
        onSendReminder={handleSendReminder} />

      }

      {showReminderModal &&
      <ReminderModal
        isOpen={showReminderModal}
        onClose={() => {
          setShowReminderModal(false);
          setSelectedInterview(null);
        }}
        interview={selectedInterview}
        onSend={(reminderData) => {
          console.log('Reminder sent:', reminderData);
          setShowReminderModal(false);
          setSelectedInterview(null);
        }} />

      }
    </MainLayout>);

};

export default InterviewSchedulingManagement;